import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Home } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { CountryDropdown } from './dropdowns/CountryDropdown';
import { LeagueDropdown } from './dropdowns/LeagueDropdown';
import { TeamsDropdown } from './dropdowns/TeamsDropdown';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { MatchData } from './types/selector-types';
interface TeamSelectorProps {
  currentTeam?: string;
}
export const TeamSelector: React.FC<TeamSelectorProps> = ({
  currentTeam
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCountry, setSelectedCountry] = React.useState<string | null>(null);
  const [selectedLeague, setSelectedLeague] = React.useState<string | null>(null);
  const {
    data: countriesData
  } = useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('countries').select('name').order('display_order');
      if (error) throw error;
      return data || [];
    },
    staleTime: 30000,
    refetchOnWindowFocus: false
  });
  const {
    data: savedStates = []
  } = useQuery({
    queryKey: ['saved-states'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('saved_states').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      return data || [];
    },
    staleTime: 30000,
    refetchOnWindowFocus: false
  });
  const countries = React.useMemo(() => {
    if (!countriesData) return [];
    return countriesData.map(country => country.name);
  }, [countriesData]);
  const leagues = React.useMemo(() => {
    if (!savedStates || !selectedCountry) return [];
    const uniqueLeagues = new Set<string>();
    savedStates.forEach(state => {
      if (state.country === selectedCountry && state.league) {
        uniqueLeagues.add(state.league);
      }
    });
    return Array.from(uniqueLeagues).sort();
  }, [savedStates, selectedCountry]);
  const filteredTeams = React.useMemo(() => {
    if (!savedStates || !selectedCountry || !selectedLeague) return [];
    const teamsSet = new Set<string>();
    let latestYear = '';

    // Find the latest year for the selected country and league
    savedStates.forEach(state => {
      if (state.country === selectedCountry && state.league === selectedLeague && state.year && state.year > latestYear) {
        latestYear = state.year;
      }
    });

    // Get teams only from the latest year
    savedStates.forEach(state => {
      if (state.country === selectedCountry && state.league === selectedLeague && state.year === latestYear && Array.isArray(state.data)) {
        state.data.forEach((match: MatchData) => {
          if (match.HomeTeam) teamsSet.add(match.HomeTeam);
          if (match.AwayTeam) teamsSet.add(match.AwayTeam);
        });
      }
    });
    return Array.from(teamsSet).sort();
  }, [savedStates, selectedCountry, selectedLeague]);
  const handleTeamSelect = (team: string) => {
    navigate(`/team/${team}`);
  };
  const isTeamPage = window.location.pathname.startsWith('/team/');
  const isHomePage = location.pathname === '/';
  return <div className="w-full">
      <div className="flex justify-between items-start mb-4">
        <div></div>
        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-end space-x-3">
            {!isHomePage}
            <CountryDropdown countries={countries} selectedCountry={selectedCountry} onCountrySelect={setSelectedCountry} />
          </div>
          {selectedCountry && <div className="flex justify-end">
              <LeagueDropdown leagues={leagues} selectedLeague={selectedLeague} onLeagueSelect={setSelectedLeague} />
            </div>}
          {selectedCountry && selectedLeague && <div className="flex justify-end">
              <TeamsDropdown teams={filteredTeams} currentTeam={currentTeam} onTeamSelect={handleTeamSelect} />
            </div>}
        </div>
      </div>
    </div>;
};
export default TeamSelector;